import { createStore } from 'vuex'
import baselineData from '@/data/baseline.json'

// 从 localStorage 获取保存的场景数据，如果没有则使用基准场景
const getSavedScenarios = () => {
  const savedScenarios = localStorage.getItem('scenarios')
  if (savedScenarios) {
    // 确保基准场景数据始终来自 baseline.json
    const scenarios = JSON.parse(savedScenarios)
    scenarios.baseline = baselineData.baseline
    return scenarios
  }
  return {
    baseline: baselineData.baseline
  }
}

export default createStore({
  state: {
    scenarios: getSavedScenarios(),
    currentData: null,
    filters: {
      province: '',
      year: '',
      scenario: ''
    }
  },
  
  getters: {
    // 从baseline.json中获取化石能源比例
    getSectorFossilRatio: (state) => (scenario, province, sector) => {
      try {
        return state.scenarios[scenario][province].sectors[sector].fossil_ratio
      } catch (error) {
        console.error(`Failed to get fossil ratio for ${province} ${sector}:`, error)
        return { coal: 0, oil: 0, gas: 0 }
      }
    },
    
    // 计算农业能耗
    getAgricultureEnergy: (state) => (scenario, province, year) => {
      try {
        const gdp = state.scenarios[scenario][province].gdp_composition.primary[year]
        const intensity = state.scenarios[scenario][province].sectors.agriculture.intensity[year]
        return gdp * intensity
      } catch (error) {
        console.error('Failed to calculate agriculture energy:', error)
        return 0
      }
    },

    // 计算工业能耗
    getIndustryEnergy: (state) => (scenario, province, year) => {
      try {
        const gdp = state.scenarios[scenario][province].gdp_composition.secondary[year]
        const intensity = state.scenarios[scenario][province].sectors.industry.intensity[year]
        return gdp * intensity
      } catch (error) {
        console.error('Failed to calculate industry energy:', error)
        return 0
      }
    },

    // 计算建筑能耗
    getConstructionEnergy: (state) => (scenario, province, year) => {
      try {
        const gdp = state.scenarios[scenario][province].gdp_composition.secondary[year]
        const intensity = state.scenarios[scenario][province].sectors.construction.intensity[year]
        return gdp * intensity
      } catch (error) {
        console.error('Failed to calculate construction energy:', error)
        return 0
      }
    },

    // 计算交通能耗
    getTransportEnergy: (state) => (scenario, province, year) => {
      try {
        const turnover = state.scenarios[scenario][province].sectors.transport.turnover[year]
        const intensity = state.scenarios[scenario][province].sectors.transport.intensity[year]
        return turnover * intensity
      } catch (error) {
        console.error('Failed to calculate transport energy:', error)
        return 0
      }
    },

    // 计算服务业能耗
    getServiceEnergy: (state) => (scenario, province, year) => {
      try {
        const gdp = state.scenarios[scenario][province].gdp_composition.tertiary[year]
        const intensity = state.scenarios[scenario][province].sectors.service.intensity[year]
        return gdp * intensity
      } catch (error) {
        console.error('Failed to calculate service energy:', error)
        return 0
      }
    },

    // 计算居民能耗
    getResidentialEnergy: (state) => (scenario, province, year) => {
      try {
        const population = state.scenarios[scenario][province].pop[year]
        const intensity = state.scenarios[scenario][province].sectors.residential.intensity[year]
        return population * intensity
      } catch (error) {
        console.error('Failed to calculate residential energy:', error)
        return 0
      }
    },

    // 计算其他能耗
    getOthersEnergy: (state) => (scenario, province, year) => {
      try {
        const gdp = state.scenarios[scenario][province].gdp_composition.tertiary[year]
        const intensity = state.scenarios[scenario][province].sectors.others.intensity[year]
        return gdp * intensity
      } catch (error) {
        console.error('Failed to calculate others energy:', error)
        return 0
      }
    }
  },

  mutations: {
    SET_CURRENT_DATA(state, data) {
      state.currentData = data
    },
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters }
    },
    ADD_SCENARIO(state, { name, data }) {
      state.scenarios = {
        ...state.scenarios,
        [name]: data
      }
      // 保存到 localStorage
      localStorage.setItem('scenarios', JSON.stringify(state.scenarios))
    },
    DELETE_SCENARIO(state, scenarioName) {
      const { [scenarioName]: deleted, ...rest } = state.scenarios
      state.scenarios = rest
      // 保存到 localStorage
      localStorage.setItem('scenarios', JSON.stringify(state.scenarios))
    },
    SET_SCENARIOS(state, scenarios) {
      state.scenarios = scenarios
    }
  },
  actions: {
    loadProvinceData({ commit, state }, { scenario, province, year }) {
      try {
        const yearStr = year.toString()
        const scenarioData = state.scenarios[scenario]
        if (!scenarioData) {
          throw new Error(`Scenario ${scenario} not found`)
        }

        const provinceData = scenarioData[province]
        if (!provinceData) {
          throw new Error(`Province ${province} not found`)
        }

        // 构建特定年份的数据,添加fossil_ratio
        const yearData = {
          pop: provinceData.pop[yearStr],
          gdp: provinceData.gdp[yearStr],
          primaryIndustry: provinceData.gdp_composition?.primary?.[yearStr] || 0,
          secondaryIndustry: provinceData.gdp_composition?.secondary?.[yearStr] || 0,
          tertiaryIndustry: provinceData.gdp_composition?.tertiary?.[yearStr] || 0,
          sectors: {}
        }

        // 加载部门数据时包含fossil_ratio
        Object.entries(provinceData.sectors).forEach(([sector, data]) => {
          yearData.sectors[sector] = {
            intensity: data.intensity[yearStr],
            electrification: data.electrification[yearStr],
            hydrogenation: data.hydrogenation[yearStr],
            fossil_ratio: data.fossil_ratio // 使用baseline.json中的fossil_ratio
          }
        })

        commit('SET_CURRENT_DATA', yearData)
        commit('SET_FILTERS', { province, year, scenario })
        
        return yearData
      } catch (error) {
        console.error('Failed to load province data:', error)
        commit('SET_CURRENT_DATA', null)
        throw error
      }
    },
    createNewScenario({ commit, state }, { name }) {
      const baselineData = JSON.parse(JSON.stringify(state.scenarios.baseline))
      commit('ADD_SCENARIO', {
        name,
        data: baselineData
      })
    },
    deleteScenario({ commit }, scenarioName) {
      commit('DELETE_SCENARIO', scenarioName)
    },
    saveChanges({ state, commit }) {
      try {
        const { scenario, province, year } = state.filters
        if (!scenario || !province || !year) {
          throw new Error('Missing required filters')
        }

        // 添加对 baseline 的保护
        if (scenario === 'baseline') {
          throw new Error('基准情景数据不允许修改')
        }

        // 深拷贝当前场景数据
        const scenarios = JSON.parse(JSON.stringify(state.scenarios))
        const currentData = state.currentData

        if (currentData) {
          // 确保数据结构存在
          if (!scenarios[scenario]) {
            scenarios[scenario] = JSON.parse(JSON.stringify(state.scenarios.baseline))
          }
          if (!scenarios[scenario][province]) {
            scenarios[scenario][province] = JSON.parse(JSON.stringify(state.scenarios.baseline[province]))
          }

          // 更新基础数据
          scenarios[scenario][province].pop[year] = currentData.pop
          scenarios[scenario][province].gdp[year] = currentData.gdp
          
          // 确保数据结构存在
          if (!scenarios[scenario][province].gdp_composition) {
            scenarios[scenario][province].gdp_composition = {
              primary: {},
              secondary: {},
              tertiary: {}
            }
          }
          
          // 保存三个产业数据
          scenarios[scenario][province].gdp_composition.primary[year] = currentData.primaryIndustry
          scenarios[scenario][province].gdp_composition.secondary[year] = currentData.secondaryIndustry
          scenarios[scenario][province].gdp_composition.tertiary[year] = currentData.tertiaryIndustry

          // 更新部门数据
          Object.entries(currentData.sectors).forEach(([sector, data]) => {
            scenarios[scenario][province].sectors[sector].intensity[year] = data.intensity
            scenarios[scenario][province].sectors[sector].electrification[year] = data.electrification
            scenarios[scenario][province].sectors[sector].hydrogenation[year] = data.hydrogenation
            // 保持原有的fossil_ratio不变
            scenarios[scenario][province].sectors[sector].fossil_ratio = 
              state.scenarios[scenario][province].sectors[sector].fossil_ratio
          })

          // 更新 store 中的场景数据
          commit('SET_SCENARIOS', scenarios)
          
          // 保存到 localStorage
          localStorage.setItem('scenarios', JSON.stringify(scenarios))
        }

        return true
      } catch (error) {
        console.error('Failed to save changes:', error)
        throw error
      }
    },
    // 可以添加更新fossil_ratio的action
    updateFossilRatio({ state, commit }, { scenario, province, sector, ratios }) {
      try {
        const scenarios = JSON.parse(JSON.stringify(state.scenarios))
        scenarios[scenario][province].sectors[sector].fossil_ratio = ratios
        commit('SET_SCENARIOS', scenarios)
        localStorage.setItem('scenarios', JSON.stringify(scenarios))
      } catch (error) {
        console.error('Failed to update fossil ratio:', error)
        throw error
      }
    }
  }
}) 

// 修改日志输出
console.log('Loaded scenarios:', getSavedScenarios()) 